"use strict";
/*!
Copyright (C) 2020 Evolved Binary

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <https://www.gnu.org/licenses/>.
*/
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createPrFromContribution = exports.getUserInfo = exports.exchangeOAuthCodeForAccessToken = exports.fetchAndTransform = exports.transformGitHubDocumentToProsemirrorJson = exports.fetchRawDocumentFromGitHub = void 0;
const lwdita_xdita_1 = require("@evolvedbinary/lwdita-xdita");
const document_1 = require("../document");
const request_1 = require("./request");
const urijs_1 = __importDefault(require("urijs"));
/**
 * Fetches the raw content of a document from a GitHub repository.
 *
 * @param config - configuration
 * @param urlParams - Object containing ghrepo, source, branch.
 * @returns A promise that resolves to the raw content of the document as a string.
 *
 * @remarks
 * This function currently fetches the document from the 'main' branch of the repository.
 * should use the GitHub API to dynamically determine the default branch of the repository.
 */
const fetchRawDocumentFromGitHub = (config, urlParams) => __awaiter(void 0, void 0, void 0, function* () {
    const { ghrepo, source, branch } = urlParams;
    // GitHub changes the raw api URL from `main` to `refs/heads/main` 
    // https://raw.githubusercontent.com/evolvedbinary/prosemirror-lwdita/main/packages/prosemirror-lwdita-demo/example-xdita/02-short-file.xml
    // https://raw.githubusercontent.com/evolvedbinary/prosemirror-lwdita/refs/heads/main/packages/prosemirror-lwdita-demo/example-xdita/02-short-file.xml
    const url = `https://raw.githubusercontent.com/${ghrepo}/refs/heads/${branch}/${source}`;
    const response = yield fetch(url);
    if (!response.ok) {
        (0, request_1.showErrorPage)(config, 'fileNotFoundError', urlParams.referrer);
    }
    return response.text();
});
exports.fetchRawDocumentFromGitHub = fetchRawDocumentFromGitHub;
/**
 * Transforms a raw GitHub document into a ProseMirror state save.
 *
 * @param rawDocument - The raw xdita document as a string.
 * @returns A promise that resolves to a record containing the ProseMirror state save.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const transformGitHubDocumentToProsemirrorJson = (rawDocument) => __awaiter(void 0, void 0, void 0, function* () {
    // convert the raw xdita document to jdita
    const jdita = yield (0, lwdita_xdita_1.xditaToJdita)(rawDocument);
    // convert the jdita document to prosemirror state save
    const prosemirrorJson = yield (0, document_1.document)(jdita);
    return prosemirrorJson;
});
exports.transformGitHubDocumentToProsemirrorJson = transformGitHubDocumentToProsemirrorJson;
/**
 * Fetches a raw document from a GitHub repository and transforms it into a ProseMirror JSON document.
 *
 * @param config - configuration
 * @param urlParams - Object containing ghrepo, source, branch.
 * @returns A promise that resolves to the transformed ProseMirror JSON document.
 */
const fetchAndTransform = (config, urlParams) => __awaiter(void 0, void 0, void 0, function* () {
    const rawDoc = yield (0, exports.fetchRawDocumentFromGitHub)(config, urlParams);
    // update the document with the relative path
    const updatedDoc = rawDoc.replace(/href="([^"]+)"/g, (_match, url) => {
        // https://www.npmjs.com/package/urijs
        return `href="${(0, urijs_1.default)(url).absoluteTo(urlParams.referrer).href()}"`;
    });
    try {
        const jsonDoc = yield (0, exports.transformGitHubDocumentToProsemirrorJson)(updatedDoc);
        return jsonDoc;
    }
    catch (_error) {
        (0, request_1.showErrorPage)(config, 'incompatibleXditaFile', urlParams.referrer);
    }
});
exports.fetchAndTransform = fetchAndTransform;
/**
 * Exchanges an OAuth code for an access token.
 *
 * @param config - configuration
 * @param _localization - localization
 * @param code - The OAuth code to exchange for an access token.
 * @returns A promise that resolves to the access token as a string.
 * @throws Will throw an error if the fetch request fails or if the response is not in the expected format.
 */
const exchangeOAuthCodeForAccessToken = (config, _localization, code) => __awaiter(void 0, void 0, void 0, function* () {
    // build the URL to exchange the code for an access token
    const url = config.server.api.baseUrl + config.server.api.endpoint.token + `?code=${code}`;
    // fetch the access token
    const response = yield fetch(url);
    const json = yield response.json();
    return {
        token: json.token,
        installation: json.installation
    };
});
exports.exchangeOAuthCodeForAccessToken = exchangeOAuthCodeForAccessToken;
/**
 * Fetches user information from the backend API.
 *
 * @param config - configuration
 * @param _localization - localization
 * @param token - The authorization token to access the GitHub API.
 * @returns A promise that resolves to a record containing user information.
 */
const getUserInfo = (config, _localization, token) => __awaiter(void 0, void 0, void 0, function* () {
    const url = config.server.api.baseUrl + config.server.api.endpoint.user;
    const response = yield fetch(url, {
        headers: {
            'authorization': `Bearer ${token}`
        }
    });
    const json = yield response.json();
    return json;
});
exports.getUserInfo = getUserInfo;
/**
 * Publishes a document to a specified GitHub repository.
 * Makes a POST request to the `/api/github/integration` endpoint with the necessary details to create a pull request.
 *
 * @param config - configuration
 * @param localization - localization
 * @param ghrepo - The GitHub repository in the format "owner/repo".
 * @param source - The path to the source document.
 * @param branch - The branch used as base for the PR.
 * @param title - The title of the pull request and the commit message.
 * @param desc - The description of the pull request.
 * @param changedDocument - The content of the changed document.
 * @returns A promise that resolves when the document has been published.
 */
const createPrFromContribution = (config, localization, ghrepo, source, branch, changedDocument, title, desc) => __awaiter(void 0, void 0, void 0, function* () {
    const token = global.token;
    const authenticatedUserInfo = yield (0, exports.getUserInfo)(config, localization, token);
    const owner = ghrepo.split('/')[0];
    const repo = ghrepo.split('/')[1];
    const newOwner = authenticatedUserInfo.login;
    const date = new Date();
    const newBranch = config.git.branchPrefix + `${date.getFullYear()}${date.getMonth()}${date.getDate()}${date.getHours()}${date.getMinutes()}${date.getSeconds()}`;
    const commitMessage = title;
    const path = source;
    const content = changedDocument;
    const change = {
        path,
        content
    };
    const body = `${desc}` + config.git.commitMessageSuffix;
    // make a post request to  /api/github/integration
    const response = yield fetch(config.server.api.baseUrl + config.server.api.endpoint.integration, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
            owner,
            repo,
            newOwner,
            branch,
            newBranch,
            commitMessage,
            change,
            title,
            body
        })
    });
    const json = yield response.json();
    if (!json.url) {
        throw new Error(localization.t("error.toastGitHubPR") + json.error);
    }
    return json.url;
});
exports.createPrFromContribution = createPrFromContribution;
//# sourceMappingURL=github.plugin.js.map